import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Divider from "@material-ui/core/Divider";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	ticketDeletedEvent,
	ticketReadEvent,
	ticketUpdatedEvent,
	ticketsBulkUpdatedEvent
} from "../../utils/ticketEvents";
import { ensureWebPushSubscription } from "../../services/webPush";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(0.5, 1.5),
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
	},
	soundSwitch: {
		marginLeft: 0,
		marginRight: 0,
		width: "100%",
		justifyContent: "space-between",
	},
	internalNotification: {
		cursor: "pointer",
	},
	internalNotificationPrimary: {
		fontWeight: 600,
	},
}));

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const soundStorageKey = `notificationSoundEnabled:${user?.id || "default"}`;
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [internalNotifications, setInternalNotifications] = useState([]);
	const [soundEnabled, setSoundEnabled] = useState(() => {
		const storedValue = localStorage.getItem("notificationSoundEnabled:default");
		return storedValue === null ? true : storedValue === "true";
	});

	const { tickets } = useTickets({ withUnreadMessages: "true" });
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();
	const soundEnabledRef = useRef(soundEnabled);

	const historyRef = useRef(history);

	useEffect(() => {
		const storedValue = localStorage.getItem(soundStorageKey);
		if (storedValue !== null) {
			setSoundEnabled(storedValue === "true");
		}
	}, [soundStorageKey]);

	useEffect(() => {
		soundEnabledRef.current = soundEnabled;
		localStorage.setItem(soundStorageKey, String(soundEnabled));
	}, [soundEnabled, soundStorageKey]);

	useEffect(() => {
		soundAlertRef.current = play;
	}, [play]);

	useEffect(() => {
		if (!user?.id) return;

		ensureWebPushSubscription().catch(error => {
			console.error("Could not enable web push notifications", error);
		});
	}, [user?.id]);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();
		const userQueueIds = (user?.queues || []).map(queue => Number(queue.id));

		const ticketBelongsToNotifications = ticket => {
			if (!ticket) return false;

			const queueAllowed =
				!ticket.queueId || userQueueIds.includes(Number(ticket.queueId));
			const userAllowed =
				ticket.status === "pending" ||
				Number(ticket.userId) === Number(user?.id) ||
				!ticket.userId;

			return (
				Number(ticket.unreadMessages || 0) > 0 &&
				queueAllowed &&
				userAllowed &&
				ticket.status !== "closed"
			);
		};

		const removeTicketNotification = ticketId => {
			setNotifications(prevState =>
				prevState.filter(ticket => Number(ticket.id) !== Number(ticketId))
			);
		};

		const upsertTicketNotification = ticket => {
			setNotifications(prevState => {
				const ticketIndex = prevState.findIndex(
					item => Number(item.id) === Number(ticket.id)
				);

				if (ticketIndex !== -1) {
					const nextState = [...prevState];
					nextState[ticketIndex] = ticket;
					return nextState;
				}

				return [ticket, ...prevState];
			});
		};

		socket.on("connect", () => socket.emit("joinNotification"));

		socket.on("ticket", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				removeTicketNotification(data.ticketId);
				return;
			}

			if (data.action === "update") {
				if (ticketBelongsToNotifications(data.ticket)) {
					upsertTicketNotification(data.ticket);
				} else {
					removeTicketNotification(data.ticket?.id);
				}
			}
		});

		socket.on("appMessage", data => {
			const isCurrentVisibleTicket =
				Number(data.message?.ticketId) === Number(ticketIdRef.current) &&
				document.visibilityState === "visible";

			if (data.action === "create" && isCurrentVisibleTicket) {
				removeTicketNotification(data.message.ticketId);
				return;
			}

			if (
				data.action === "create" &&
				!data.message.read &&
				ticketBelongsToNotifications({
					...data.ticket,
					unreadMessages: data.ticket.unreadMessages || 1
				})
			) {
				upsertTicketNotification(data.ticket);

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});

		socket.on("internalMessageNotification", data => {
			if (data.action !== "create" || !data.message || !data.chat) return;

			setInternalNotifications(prevState => {
				const notification = {
					id: data.message.id,
					chatId: data.chat.id,
					chatName: data.chat.name,
					body: data.message.body || data.message.mediaUrl || "Archivo",
					userName: data.message.user?.name || "",
					createdAt: data.message.createdAt,
				};
				const notificationIndex = prevState.findIndex(
					item => item.id === notification.id
				);
				if (notificationIndex !== -1) {
					prevState[notificationIndex] = notification;
					return [...prevState];
				}
				return [notification, ...prevState];
			});

			const shouldNotNotificate =
				historyRef.current.location.pathname === "/internal-chat" &&
				document.visibilityState === "visible";

			if (shouldNotNotificate) return;

			handleInternalNotification(data);
		});

		const handleTicketRead = event => {
			const ticketId = event.detail?.ticketId || event.detail?.ticket?.id;
			if (ticketId) {
				removeTicketNotification(ticketId);
			}
		};

		const handleTicketsBulkUpdated = event => {
			const updatedTickets = Array.isArray(event.detail?.tickets)
				? event.detail.tickets
				: [];

			updatedTickets.forEach(ticket => {
				if (ticketBelongsToNotifications(ticket)) {
					upsertTicketNotification(ticket);
				} else {
					removeTicketNotification(ticket.id);
				}
			});
		};

		const handleTicketUpdated = event => {
			const ticket = event.detail?.ticket;
			if (!ticket) return;

			if (ticketBelongsToNotifications(ticket)) {
				upsertTicketNotification(ticket);
			} else {
				removeTicketNotification(ticket.id);
			}
		};

		const handleTicketDeleted = event => {
			const ticketId = event.detail?.ticketId;
			if (ticketId) {
				removeTicketNotification(ticketId);
			}
		};

		window.addEventListener(ticketReadEvent, handleTicketRead);
		window.addEventListener(ticketUpdatedEvent, handleTicketUpdated);
		window.addEventListener(ticketsBulkUpdatedEvent, handleTicketsBulkUpdated);
		window.addEventListener(ticketDeletedEvent, handleTicketDeleted);

		return () => {
			window.removeEventListener(ticketReadEvent, handleTicketRead);
			window.removeEventListener(ticketUpdatedEvent, handleTicketUpdated);
			window.removeEventListener(ticketsBulkUpdatedEvent, handleTicketsBulkUpdated);
			window.removeEventListener(ticketDeletedEvent, handleTicketDeleted);
			socket.disconnect();
		};
	}, [user]);

	const handleNotifications = () => {
		if (soundEnabledRef.current) {
			soundAlertRef.current();
		}
	};

	const handleInternalNotification = () => {
		if (soundEnabledRef.current) {
			soundAlertRef.current();
		}
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	const handleInternalNotificationClick = notification => {
		setInternalNotifications(prevState =>
			prevState.filter(item => item.chatId !== notification.chatId)
		);
		handleClickAway();
		history.push("/internal-chat");
	};

	const notificationCount = notifications.length + internalNotifications.length;

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notificationCount} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<div className={classes.header}>
					<FormControlLabel
						className={classes.soundSwitch}
						label="Reproducir sonido"
						labelPlacement="start"
						control={
							<Switch
								size="small"
								color="primary"
								checked={soundEnabled}
								onChange={event => setSoundEnabled(event.target.checked)}
							/>
						}
					/>
				</div>
				<List dense className={classes.tabContainer}>
					{notificationCount === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						<>
							{internalNotifications.map(notification => (
								<ListItem
									button
									key={`internal-${notification.id}`}
									className={classes.internalNotification}
									onClick={() => handleInternalNotificationClick(notification)}
								>
									<ListItemText
										primary={`${notification.chatName} - ${notification.userName}`}
										secondary={`${notification.body} - ${format(
											new Date(notification.createdAt),
											"HH:mm"
										)}`}
										primaryTypographyProps={{
											className: classes.internalNotificationPrimary,
											noWrap: true,
										}}
										secondaryTypographyProps={{ noWrap: true }}
									/>
								</ListItem>
							))}
							{internalNotifications.length > 0 && notifications.length > 0 && (
								<Divider />
							)}
							{notifications.map(ticket => (
								<NotificationTicket key={ticket.id}>
									<TicketListItem ticket={ticket} />
								</NotificationTicket>
							))}
						</>
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;

